import { Injectable, OnDestroy, inject } from '@angular/core';
import { ExternalApis } from '@app/core/services/api/API.service';
import { BehaviorSubject } from 'rxjs';
import { UtilService } from '../../util.service';
import { take } from 'rxjs/operators';
import * as subscriptions from '@app/core/graphql/subscriptions';
import { AmplifyApiService, ExternalApiService } from '@app/core/api/services';

@Injectable({
    providedIn: 'root'
})
export class ExternalApisService implements OnDestroy {
    private _externalApis$ = new BehaviorSubject<ExternalApis[]>([]);
    private _loading$ = new BehaviorSubject<boolean>(true);
    private externalApis: ExternalApis[] = [];
    private subscriptions: any[] = [];
    private _externalApiService = inject(ExternalApiService);
    private _amplifyApiService = inject(AmplifyApiService);

    private _isLoading = false;

    constructor(private _utilService: UtilService) {
        this.subscriptions.push(
            this._amplifyApiService
                .subscribe({ query: subscriptions.onCreateExternalApis })
                .subscribe(observer => {
                    const data = observer.data.onCreateExternalApis;
                    this.externalApis.push(data);
                    this.externalApis = this._utilService.sortJSON(
                        this.externalApis,
                        'name',
                        'asc'
                    );
                    this._externalApis$.next(this.externalApis);
                }),
            this._amplifyApiService
                .subscribe({ query: subscriptions.onUpdateExternalApis })
                .subscribe(observer => {
                    const data = observer.data.onUpdateExternalApis;
                    this.externalApis = this.externalApis.map(
                        (externalApi: ExternalApis) => {
                            return externalApi.id === data.id
                                ? data
                                : externalApi;
                        }
                    );
                    this.externalApis = this._utilService.sortJSON(
                        this.externalApis,
                        'name',
                        'asc'
                    );
                    this._externalApis$.next(this.externalApis);
                }),
            this._amplifyApiService
                .subscribe({ query: subscriptions.onDeleteExternalApis })
                .subscribe(observer => {
                    const data = observer.data.onDeleteExternalApis;
                    this.externalApis = this.externalApis.filter(
                        (externalApi: ExternalApis) => {
                            return externalApi.id !== data.id;
                        }
                    );
                    this._externalApis$.next(this.externalApis);
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    get externalApis$() {
        return this._externalApis$.asObservable();
    }

    get loading$() {
        return this._loading$.asObservable();
    }

    async listExternalApis() {
        if (this._isLoading) {
            return;
        }

        const existingApis = this._externalApis$.getValue();
        if (existingApis && existingApis.length > 0) {
            return;
        }

        this._isLoading = true;
        try {
            this._loading$.next(true);
            this.externalApis = await this.listExternalApisBD();
        } catch (err) {
            this.externalApis = [];
        } finally {
            this._externalApis$.next(this.externalApis);
            this._loading$.next(false);
            this._isLoading = false;
        }
    }

    async findExternalApiById(id: string): Promise<ExternalApis | undefined> {
        try {
            const externalApis = await this.externalApis$
                .pipe(take(1))
                .toPromise();
            return externalApis.find(externalAPI => externalAPI.id === id);
        } catch (error) {
            console.error('Error finding externalAPI:', error);
            return undefined;
        }
    }

    private async listExternalApisBD(): Promise<ExternalApis[]> {
        const items = await this._externalApiService.listExternalApis(50);
        return this._utilService.sortJSON(items ?? [], 'name', 'asc');
    }
}
